import * as THREE from 'three';
import { PointCloudTree, PointCloudTreeNode } from './point-cloud-tree.js';
import { PointCloudOctreeGeometryNode } from './point-cloud-octree-geometry.js';
import { Utils } from '../shared/utils.js';
import { PointCloudMaterial } from './materials/point-cloud-material.js';
import { ProfileRequest, ProfileCallback } from './profile-request.js';
import { PointShape, ClipTask } from '../shared/defines.js';
import { PointAttribute } from '../types/PointAttributes.js';
import { Profile } from './tools/profile.js';
import type { Renderer } from './point-cloud-renderer.js';
import type { OctreeGeometryLike, OctreeGeometryNodeLike } from '../types/octree-geometry';

type PointCloudChildNode = PointCloudOctreeNode | OctreeGeometryNodeLike;

interface PickParams {
  pickWindowSize?: number;
  pickOutsideClipRegion?: boolean;
  pickClipped?: boolean;
  width?: number;
  height?: number;
  pointSizeType?: number;
  pointSize?: number;
  x?: number;
  y?: number;
  all?: boolean;
}

/** Minimal shape of the Viewer required by pick(); avoids a hard dependency on viewer/viewer.ts. */
interface PickViewerLike {
  renderer: THREE.WebGLRenderer;
  getRawPRenderer(): Renderer;
}

/** Shape of THREE's internal (undocumented) compiled shader program object. */
interface WebGLProgramLike {
  program: WebGLProgram;
  getUniforms(): {
    map: Record<
      string,
      | { setValue(gl: WebGLRenderingContext | WebGL2RenderingContext, value: unknown): void }
      | undefined
    >;
  };
}

export class PointCloudOctreeNode extends PointCloudTreeNode {
  children: PointCloudChildNode[];
  sceneNode: THREE.Points | null;
  octree: PointCloudOctree | null;
  geometryNode!: OctreeGeometryNodeLike;
  pointcloud!: PointCloudOctree;
  pcIndex?: number;
  boundingBoxNode?: THREE.Object3D;
  _transformVersion?: number;

  constructor() {
    super();

    this.children = [];
    this.sceneNode = null;
    this.octree = null;
  }

  getNumPoints(): number {
    return this.geometryNode.numPoints;
  }

  isLoaded(): boolean {
    return true;
  }

  isTreeNode(): boolean {
    return true;
  }

  isGeometryNode(): boolean {
    return false;
  }

  getLevel(): number | null {
    return this.geometryNode.level;
  }

  getBoundingSphere(): THREE.Sphere {
    return this.geometryNode.boundingSphere;
  }

  getBoundingBox(): THREE.Box3 {
    return this.geometryNode.boundingBox;
  }

  getChildren(): PointCloudChildNode[] {
    let children: PointCloudChildNode[] = [];

    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i]);
      }
    }

    return children;
  }

  getPointsInBox(boxNode: THREE.Object3D): THREE.Vector3[] | null {
    if (!this.sceneNode) {
      return null;
    }

    let buffer = this.geometryNode.geometry;
    if (!buffer) {
      return null;
    }

    const positionAttribute = buffer.getAttribute('position');

    let worldToBox = boxNode.matrixWorld.clone().invert();
    let objectToBox = new THREE.Matrix4().multiplyMatrices(worldToBox, this.sceneNode.matrixWorld);

    let inBox: THREE.Vector3[] = [];

    let pos = new THREE.Vector4();
    for (let i = 0; i < positionAttribute.count; i++) {
      let x = positionAttribute.getX(i);
      let y = positionAttribute.getY(i);
      let z = positionAttribute.getZ(i);

      pos.set(x, y, z, 1);
      pos.applyMatrix4(objectToBox);

      if (-0.5 < pos.x && pos.x < 0.5) {
        if (-0.5 < pos.y && pos.y < 0.5) {
          if (-0.5 < pos.z && pos.z < 0.5) {
            pos.set(x, y, z, 1).applyMatrix4(this.sceneNode.matrixWorld);
            inBox.push(new THREE.Vector3(pos.x, pos.y, pos.z));
          }
        }
      }
    }

    return inBox;
  }

  get name(): string {
    return this.geometryNode.name;
  }
}

interface PointCloudOctreeEventMap extends THREE.Object3DEventMap {
  visibility_changed: { pointcloud: PointCloudOctree };
  name_changed: { name: string; pointcloud: PointCloudOctree };
}

export class PointCloudOctree extends PointCloudTree<PointCloudOctreeEventMap> {
  pointBudget: number;
  pcoGeometry: OctreeGeometryLike;
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  material: PointCloudMaterial;
  visiblePointsTarget: number;
  minimumNodePixelSize: number;
  level: number;
  showBoundingBox: boolean;
  boundingBoxNodes: THREE.Object3D[];
  loadQueue: unknown[];
  visibleBounds: THREE.Box3;
  visibleNodes: PointCloudOctreeNode[];
  visibleGeometry: OctreeGeometryNodeLike[];
  generateDEM: boolean;
  profileRequests: ProfileRequest[];
  _visible: boolean;
  projection: string | null;
  fallbackProjection: string | null;
  numVisibleNodes: number;
  numVisiblePoints: number;
  deepestVisibleLevel: number;
  visibleNodeTextureOffsets?: Map<PointCloudOctreeNode, number>;
  pickState?: {
    renderTarget: THREE.WebGLRenderTarget;
    material: PointCloudMaterial;
    scene: THREE.Scene;
  };
  signedDistanceField?: { resolution: number; field: Float32Array };
  maxLevel?: number;
  dem?: unknown;

  constructor(geometry: OctreeGeometryLike, material?: PointCloudMaterial) {
    super();

    this.pointBudget = Infinity;
    this.pcoGeometry = geometry;
    this.boundingBox = this.pcoGeometry.boundingBox ?? new THREE.Box3();
    this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());
    this.material = material || new PointCloudMaterial();
    this.visiblePointsTarget = 2 * 1000 * 1000;
    this.minimumNodePixelSize = 150;
    this.level = 0;
    if (geometry.offset) {
      this.position.copy(geometry.offset);
    }
    this.updateMatrix();

    {
      let priorityQueue = ['rgba', 'rgb', 'intensity', 'classification'];
      let selected = 'rgba';

      for (let attributeName of priorityQueue) {
        let attribute = this.pcoGeometry.pointAttributes.attributes.find(
          a => a.name === attributeName,
        );

        if (!attribute) {
          continue;
        }

        let min = Array.isArray(attribute.range[0]) ? attribute.range[0] : [attribute.range[0]];
        let max = Array.isArray(attribute.range[1]) ? attribute.range[1] : [attribute.range[1]];

        let range_min = new THREE.Vector3(...(min as number[]));
        let range_max = new THREE.Vector3(...(max as number[]));
        let range = range_min.distanceTo(range_max);

        if (range === 0) {
          continue;
        }

        selected = attributeName;
        break;
      }

      this.material.activeAttributeName = selected;
    }

    this.showBoundingBox = false;
    this.boundingBoxNodes = [];
    this.loadQueue = [];
    this.visibleBounds = new THREE.Box3();
    this.visibleNodes = [];
    this.visibleGeometry = [];
    this.generateDEM = false;
    this.profileRequests = [];
    this.name = '';
    this._visible = true;
    this.numVisibleNodes = 0;
    this.numVisiblePoints = 0;
    this.deepestVisibleLevel = 0;
    this.projection = null;
    this.fallbackProjection = null;

    // Override 'visible' property with getter/setter to dispatch events
    Object.defineProperty(this, 'visible', {
      get: () => this._visible,
      set: (value: boolean) => {
        if (value !== this._visible) {
          this._visible = value;
          this.dispatchEvent({ type: 'visibility_changed', pointcloud: this });
        }
      },
      configurable: true,
    });

    {
      let box = [this.pcoGeometry.tightBoundingBox, this.getBoundingBoxWorld()].find(
        (v): v is THREE.Box3 => v !== undefined,
      )!;

      this.updateMatrixWorld(true);
      box = Utils.computeTransformedBoundingBox(box, this.matrixWorld);

      let bMin = box.min.z;
      let bMax = box.max.z;
      this.material.heightMin = bMin;
      this.material.heightMax = bMax;
    }

    // TODO read projection from file instead
    this.projection = geometry.projection ?? null;
    this.fallbackProjection = geometry.fallbackProjection ?? null;

    this.root = this.pcoGeometry.root as unknown as PointCloudTreeNode | null;
  }

  setName(name: string): void {
    if (this.name !== name) {
      this.name = name;
      this.dispatchEvent({ type: 'name_changed', name: name, pointcloud: this });
    }
  }

  getName(): string {
    return this.name;
  }

  getAttribute(name: string): PointAttribute | null {
    const attribute = this.pcoGeometry.pointAttributes.attributes.find(a => a.name === name);

    if (attribute) {
      return attribute;
    } else {
      return null;
    }
  }

  getAttributes() {
    return this.pcoGeometry.pointAttributes;
  }

  toTreeNode(
    geometryNode: OctreeGeometryNodeLike,
    parent?: PointCloudOctreeNode | null,
  ): PointCloudOctreeNode {
    let node = new PointCloudOctreeNode();
    let sceneNode = new THREE.Points(geometryNode.geometry ?? undefined, this.material);
    sceneNode.name = geometryNode.name;
    sceneNode.position.copy(geometryNode.boundingBox.min);
    sceneNode.frustumCulled = false;
    sceneNode.onBeforeRender = (
      renderer: THREE.WebGLRenderer,
      _scene: THREE.Scene,
      _camera: THREE.Camera,
      _geometry: THREE.BufferGeometry,
      material: THREE.Material,
    ) => {
      const potreeMaterial = material as PointCloudMaterial;
      const program = (renderer.properties.get(potreeMaterial) as { program?: WebGLProgramLike })
        .program;

      if (program) {
        const gl = renderer.getContext();
        gl.useProgram(program.program);

        const uniforms = program.getUniforms();

        if (uniforms.map.level) {
          let level = geometryNode.getLevel();
          potreeMaterial.uniforms['level'].value = level;
          uniforms.map.level.setValue(gl, level);
        }

        if (this.visibleNodeTextureOffsets && uniforms.map.vnStart) {
          let vnStart = this.visibleNodeTextureOffsets.get(node);
          potreeMaterial.uniforms['vnStart'].value = vnStart;
          uniforms.map.vnStart.setValue(gl, vnStart);
        }

        if (uniforms.map.pcIndex) {
          let i = node.pcIndex ? node.pcIndex : this.visibleNodes.indexOf(node);
          potreeMaterial.uniforms['pcIndex'].value = i;
          uniforms.map.pcIndex.setValue(gl, i);
        }
      }
    };

    node.geometryNode = geometryNode;
    node.sceneNode = sceneNode;
    node.pointcloud = this;
    node.children = [];
    for (const child of geometryNode.getChildren()) {
      node.children[child.index] = child;
    }

    if (!parent) {
      this.root = node;
      this.add(sceneNode);
    } else {
      let childIndex = parseInt(geometryNode.name[geometryNode.name.length - 1]);
      parent.sceneNode!.add(sceneNode);
      parent.children[childIndex] = node;
    }

    let disposeListener = function () {
      let childIndex = parseInt(geometryNode.name[geometryNode.name.length - 1]);
      parent?.sceneNode!.remove(node.sceneNode!);
      if (parent) parent.children[childIndex] = geometryNode;
    };
    geometryNode.oneTimeDisposeHandlers.push(disposeListener);

    return node;
  }

  updateVisibleBounds(): void {
    let leafNodes: PointCloudOctreeNode[] = [];
    for (let i = 0; i < this.visibleNodes.length; i++) {
      let node = this.visibleNodes[i];
      let isLeaf = true;

      for (let j = 0; j < node.children.length; j++) {
        let child = node.children[j];
        if (child instanceof PointCloudOctreeNode) {
          isLeaf = isLeaf && !child.sceneNode!.visible;
        } else if (child instanceof PointCloudOctreeGeometryNode) {
          isLeaf = true;
        }
      }

      if (isLeaf) {
        leafNodes.push(node);
      }
    }

    this.visibleBounds.min = new THREE.Vector3(Infinity, Infinity, Infinity);
    this.visibleBounds.max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    for (let i = 0; i < leafNodes.length; i++) {
      let node = leafNodes[i];

      this.visibleBounds.expandByPoint(node.getBoundingBox().min);
      this.visibleBounds.expandByPoint(node.getBoundingBox().max);
    }
  }

  updateMaterial(
    material: PointCloudMaterial,
    _visibleNodes: PointCloudOctreeNode[],
    camera: THREE.PerspectiveCamera,
    renderer: THREE.WebGLRenderer,
  ): void {
    material.fov = camera.fov * (Math.PI / 180);
    material.screenWidth = renderer.domElement.clientWidth;
    material.screenHeight = renderer.domElement.clientHeight;
    material.spacing = this.pcoGeometry.spacing;
    material.near = camera.near;
    material.far = camera.far;
    material.uniforms['octreeSize'].value = (
      this.pcoGeometry.boundingBox ?? new THREE.Box3()
    ).getSize(new THREE.Vector3()).x;
  }

  computeVisibilityTextureData(
    nodes: PointCloudOctreeNode[],
    _camera: THREE.Camera,
  ): { data: Uint8Array; offsets: Map<PointCloudOctreeNode, number> } {
    let data = new Uint8Array(nodes.length * 4);
    let visibleNodeTextureOffsets = new Map<PointCloudOctreeNode, number>();

    // copy array
    nodes = nodes.slice();

    // sort by level and index, e.g. r, r0, r3, r4, r01, r07, r30, ...
    let sort = function (a: PointCloudOctreeNode, b: PointCloudOctreeNode) {
      let na = a.geometryNode.name;
      let nb = b.geometryNode.name;
      if (na.length !== nb.length) return na.length - nb.length;
      if (na < nb) return -1;
      if (na > nb) return 1;
      return 0;
    };
    nodes.sort(sort);

    let nodeMap = new Map<string, PointCloudOctreeNode>();
    let offsetsToChild = new Array(nodes.length).fill(Infinity);

    for (let i = 0; i < nodes.length; i++) {
      let node = nodes[i];

      nodeMap.set(node.name, node);
      visibleNodeTextureOffsets.set(node, i);

      if (i > 0) {
        let parentName = node.name.slice(0, -1);
        let parent = nodeMap.get(parentName)!;
        let parentOffset = visibleNodeTextureOffsets.get(parent)!;

        let parentOffsetToChild = i - parentOffset;

        offsetsToChild[parentOffset] = Math.min(offsetsToChild[parentOffset], parentOffsetToChild);

        let index = parseInt(node.name.slice(-1));
        data[parentOffset * 4 + 0] = data[parentOffset * 4 + 0] | (1 << index);
        data[parentOffset * 4 + 1] = offsetsToChild[parentOffset] >> 8;
        data[parentOffset * 4 + 2] = offsetsToChild[parentOffset] % 256;
      }

      let density = node.geometryNode.density;

      if (typeof density === 'number' && !Number.isNaN(density)) {
        let lodOffset = Math.log2(density) / 2 - 1.5;

        let offsetUint8 = (lodOffset + 10) * 10;

        data[i * 4 + 3] = offsetUint8;
      } else {
        data[i * 4 + 3] = 100;
      }
    }

    return {
      data: data,
      offsets: visibleNodeTextureOffsets,
    };
  }

  nodeIntersectsProfile(node: PointCloudOctreeNode, profile: Profile): boolean {
    let bbWorld = node.getBoundingBox().clone().applyMatrix4(this.matrixWorld);
    let bsWorld = bbWorld.getBoundingSphere(new THREE.Sphere());

    let intersects = false;

    for (let i = 0; i < profile.points.length - 1; i++) {
      let start = new THREE.Vector3(
        profile.points[i + 0].x,
        profile.points[i + 0].y,
        bsWorld.center.z,
      );
      let end = new THREE.Vector3(
        profile.points[i + 1].x,
        profile.points[i + 1].y,
        bsWorld.center.z,
      );

      let closest = new THREE.Line3(start, end).closestPointToPoint(
        bsWorld.center,
        true,
        new THREE.Vector3(),
      );
      let distance = closest.distanceTo(bsWorld.center);

      intersects = intersects || distance < bsWorld.radius + profile.width;
    }

    return intersects;
  }

  deepestNodeAt(position: THREE.Vector3): PointCloudChildNode | null {
    const toObjectSpace = this.matrixWorld.clone().invert();

    const objPos = position.clone().applyMatrix4(toObjectSpace);

    let current: PointCloudChildNode | null = this.root as PointCloudChildNode | null;
    while (current) {
      let containingChild: PointCloudChildNode | null = null;

      for (const child of current.getChildren()) {
        if (child !== undefined) {
          if (child.getBoundingBox().containsPoint(objPos)) {
            containingChild = child;
          }
        }
      }

      if (containingChild !== null && containingChild instanceof PointCloudOctreeNode) {
        current = containingChild;
      } else {
        break;
      }
    }

    return current;
  }

  nodesOnRay(nodes: PointCloudOctreeNode[], ray: THREE.Ray): PointCloudOctreeNode[] {
    let nodesOnRay: PointCloudOctreeNode[] = [];

    let _ray = ray.clone();
    for (let i = 0; i < nodes.length; i++) {
      let node = nodes[i];
      let sphere = node.getBoundingSphere().clone().applyMatrix4(this.matrixWorld);

      if (_ray.intersectsSphere(sphere)) {
        nodesOnRay.push(node);
      }
    }

    return nodesOnRay;
  }

  updateMatrixWorld(force?: boolean): void {
    if (this.matrixAutoUpdate === true) this.updateMatrix();

    if (this.matrixWorldNeedsUpdate === true || force === true) {
      if (!this.parent) {
        this.matrixWorld.copy(this.matrix);
      } else {
        this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
      }

      this.matrixWorldNeedsUpdate = false;

      force = true;
    }
  }

  hideDescendants(object: THREE.Object3D): void {
    let stack: THREE.Object3D[] = [];
    for (let i = 0; i < object.children.length; i++) {
      let child = object.children[i];
      if (child.visible) {
        stack.push(child);
      }
    }

    while (stack.length > 0) {
      let obj = stack.shift()!;

      obj.visible = false;

      for (let i = 0; i < obj.children.length; i++) {
        let child = obj.children[i];
        if (child.visible) {
          stack.push(child);
        }
      }
    }
  }

  moveToOrigin(): void {
    this.position.set(0, 0, 0);
    this.updateMatrixWorld(true);
    let box = this.boundingBox;
    let transform = this.matrixWorld;
    let tBox = Utils.computeTransformedBoundingBox(box, transform);
    this.position.set(0, 0, 0).sub(tBox.getCenter(new THREE.Vector3()));
  }

  moveToGroundPlane(): void {
    this.updateMatrixWorld(true);
    let box = this.boundingBox;
    let transform = this.matrixWorld;
    let tBox = Utils.computeTransformedBoundingBox(box, transform);
    this.position.y += -tBox.min.y;
  }

  getBoundingBoxWorld(): THREE.Box3 {
    this.updateMatrixWorld(true);
    let box = this.boundingBox;
    let transform = this.matrixWorld;
    let tBox = Utils.computeTransformedBoundingBox(box, transform);

    return tBox;
  }

  getPointsInProfile(
    profile: Profile,
    maxDepth: number | null,
    callback?: ProfileCallback,
  ): ProfileRequest | undefined {
    if (callback) {
      let request = new ProfileRequest(this, profile, maxDepth ?? undefined, callback);
      this.profileRequests.push(request);

      return request;
    }

    // Dead branch: kept for API compatibility, never reached in practice since callers
    // always supply a callback. getProfile() below has no return value in this mode.
    let points = {
      segments: [] as Array<{
        start: THREE.Vector3;
        end: THREE.Vector3;
        points: ProfileRequest | undefined;
        project: ((position: THREE.Vector3) => THREE.Vector3) | null;
      }>,
      boundingBox: new THREE.Box3(),
      projectedBoundingBox: new THREE.Box2(),
    };

    // evaluate segments
    for (let i = 0; i < profile.points.length - 1; i++) {
      let start = profile.points[i];
      let end = profile.points[i + 1];
      let ps = this.getProfile(start, end, profile.width, maxDepth ?? undefined);

      let segment = {
        start: start,
        end: end,
        points: ps,
        project: null as ((position: THREE.Vector3) => THREE.Vector3) | null,
      };

      points.segments.push(segment);

      const psBoundingBox = (ps?.temporaryResult as { boundingBox?: THREE.Box3 } | undefined)
        ?.boundingBox;
      if (psBoundingBox) {
        points.boundingBox.expandByPoint(psBoundingBox.min);
        points.boundingBox.expandByPoint(psBoundingBox.max);
      }
    }

    // add projection functions to the segments
    let mileage = new THREE.Vector3();
    for (let i = 0; i < points.segments.length; i++) {
      let segment = points.segments[i];
      let start = segment.start;
      let end = segment.end;

      let project = (function (
        _start: THREE.Vector3,
        _end: THREE.Vector3,
        _mileage: THREE.Vector3,
        _boundingBox: THREE.Box3,
      ) {
        let start = _start;
        let end = _end;
        let mileage = _mileage;
        let boundingBox = _boundingBox;

        let xAxis = new THREE.Vector3(1, 0, 0);
        let dir = new THREE.Vector3().subVectors(end, start);
        dir.y = 0;
        dir.normalize();
        let alpha = Math.acos(xAxis.dot(dir));
        if (dir.z > 0) {
          alpha = -alpha;
        }

        return function (position: THREE.Vector3) {
          let toOrigin = new THREE.Matrix4().makeTranslation(
            -start.x,
            -boundingBox.min.y,
            -start.z,
          );
          let alignWithX = new THREE.Matrix4().makeRotationY(-alpha);
          let applyMileage = new THREE.Matrix4().makeTranslation(mileage.x, 0, 0);

          let pos = position.clone();
          pos.applyMatrix4(toOrigin);
          pos.applyMatrix4(alignWithX);
          pos.applyMatrix4(applyMileage);

          return pos;
        };
      })(start, end, mileage.clone(), points.boundingBox.clone());

      segment.project = project;

      mileage.x += new THREE.Vector3(start.x, 0, start.z).distanceTo(
        new THREE.Vector3(end.x, 0, end.z),
      );
      mileage.y += end.y - start.y;
    }

    points.projectedBoundingBox.min.x = 0;
    points.projectedBoundingBox.min.y = points.boundingBox.min.y;
    points.projectedBoundingBox.max.x = mileage.x;
    points.projectedBoundingBox.max.y = points.boundingBox.max.y;

    return undefined;
  }

  getProfile(
    start: THREE.Vector3,
    end: THREE.Vector3,
    width: number,
    depth?: number,
    callback?: ProfileCallback,
  ): ProfileRequest | undefined {
    const profile = new Profile();
    profile.points = [start, end];
    profile.width = width;
    if (!callback) {
      return undefined;
    }
    let request = new ProfileRequest(this, profile, depth, callback);
    this.profileRequests.push(request);
    return request;
  }

  getVisibleExtent(): THREE.Box3 {
    return this.visibleBounds.applyMatrix4(this.matrixWorld);
  }

  intersectsPoint(position: THREE.Vector3): boolean {
    let rootAvailable = this.pcoGeometry.root && this.pcoGeometry.root.geometry;

    if (!rootAvailable) {
      return false;
    }

    if (typeof this.signedDistanceField === 'undefined') {
      const resolution = 32;
      const field = new Float32Array(resolution ** 3).fill(Infinity);

      const positions = this.pcoGeometry.root!.geometry!.attributes['position'];
      const boundingBox = this.boundingBox;

      const n = positions.count;

      for (let i = 0; i < n; i = i + 3) {
        const x = positions.array[3 * i + 0];
        const y = positions.array[3 * i + 1];
        const z = positions.array[3 * i + 2];

        const ix = parseInt(String(Math.min(resolution * (x / boundingBox.max.x), resolution - 1)));
        const iy = parseInt(String(Math.min(resolution * (y / boundingBox.max.y), resolution - 1)));
        const iz = parseInt(String(Math.min(resolution * (z / boundingBox.max.z), resolution - 1)));

        const index = ix + iy * resolution + iz * resolution * resolution;

        field[index] = 0;
      }

      const sdf = {
        resolution: resolution,
        field: field,
      };

      this.signedDistanceField = sdf;
    }

    {
      const sdf = this.signedDistanceField;
      const boundingBox = this.boundingBox;

      const toObjectSpace = this.matrixWorld.clone().invert();

      const objPos = position.clone().applyMatrix4(toObjectSpace);

      const resolution = sdf.resolution;
      const ix = parseInt(String(resolution * (objPos.x / boundingBox.max.x)));
      const iy = parseInt(String(resolution * (objPos.y / boundingBox.max.y)));
      const iz = parseInt(String(resolution * (objPos.z / boundingBox.max.z)));

      if (ix < 0 || iy < 0 || iz < 0) {
        return false;
      }
      if (ix >= resolution || iy >= resolution || iz >= resolution) {
        return false;
      }

      const index = ix + iy * resolution + iz * resolution * resolution;

      const value = sdf.field[index];

      if (value === 0) {
        return true;
      }
    }

    return false;
  }

  pick(
    viewer: PickViewerLike,
    camera: THREE.PerspectiveCamera,
    ray: THREE.Ray,
    params: PickParams = {},
  ): Record<string, unknown> | Record<string, unknown>[] | null {
    let renderer = viewer.renderer;
    let pRenderer = viewer.getRawPRenderer();

    let pickWindowSize = params.pickWindowSize ?? 65;

    let size = renderer.getSize(new THREE.Vector2());

    let width = Math.ceil(params.width ?? size.width);
    let height = Math.ceil(params.height ?? size.height);

    let pointSizeType = params.pointSizeType ?? this.material.pointSizeType;
    let pointSize = params.pointSize ?? this.material.size;

    let nodes = this.nodesOnRay(this.visibleNodes, ray);

    if (nodes.length === 0) {
      return null;
    }

    if (!this.pickState) {
      let scene = new THREE.Scene();

      let material = new PointCloudMaterial();
      material.activeAttributeName = 'indices';

      let renderTarget = new THREE.WebGLRenderTarget(1, 1, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
      });

      this.pickState = {
        renderTarget: renderTarget,
        material: material,
        scene: scene,
      };
    }

    let pickState = this.pickState;
    let pickMaterial = pickState.material;

    {
      // update pick material
      pickMaterial.pointSizeType = pointSizeType;
      pickMaterial.shape = PointShape.PARABOLOID;

      pickMaterial.uniforms['uFilterReturnNumberRange'].value =
        this.material.uniforms['uFilterReturnNumberRange'].value;
      pickMaterial.uniforms['uFilterNumberOfReturnsRange'].value =
        this.material.uniforms['uFilterNumberOfReturnsRange'].value;
      pickMaterial.uniforms['uFilterGPSTimeClipRange'].value =
        this.material.uniforms['uFilterGPSTimeClipRange'].value;
      pickMaterial.uniforms['uFilterPointSourceIDClipRange'].value =
        this.material.uniforms['uFilterPointSourceIDClipRange'].value;

      pickMaterial.activeAttributeName = 'indices';

      pickMaterial.size = pointSize;
      pickMaterial.uniforms['minSize'].value = this.material.uniforms['minSize'].value;
      pickMaterial.uniforms['maxSize'].value = this.material.uniforms['maxSize'].value;
      pickMaterial.classification = this.material.classification;
      pickMaterial.recomputeClassification();

      if (params.pickClipped) {
        pickMaterial.clipBoxes = this.material.clipBoxes;
        pickMaterial.uniforms['clipBoxes'] = this.material.uniforms['clipBoxes'];
        if (this.material.clipTask === ClipTask.HIGHLIGHT) {
          pickMaterial.clipTask = ClipTask.NONE;
        } else {
          pickMaterial.clipTask = this.material.clipTask;
        }
        pickMaterial.clipMethod = this.material.clipMethod;
      } else {
        pickMaterial.clipBoxes = [];
      }

      this.updateMaterial(pickMaterial, nodes, camera, renderer);
    }

    pickState.renderTarget.setSize(width, height);

    let pixelPos = new THREE.Vector2(params.x, params.y);

    let gl = renderer.getContext();
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(
      parseInt(String(pixelPos.x - (pickWindowSize - 1) / 2)),
      parseInt(String(pixelPos.y - (pickWindowSize - 1) / 2)),
      parseInt(String(pickWindowSize)),
      parseInt(String(pickWindowSize)),
    );

    renderer.state.buffers.depth.setTest(pickMaterial.depthTest);
    renderer.state.buffers.depth.setMask(pickMaterial.depthWrite);
    renderer.state.setBlending(THREE.NoBlending);

    {
      // RENDER
      renderer.setRenderTarget(pickState.renderTarget);
      gl.clearColor(0, 0, 0, 0);
      renderer.clear(true, true, true);

      let tmp = this.material;
      this.material = pickMaterial;

      pRenderer.renderOctree(this, nodes, camera, pickState.renderTarget);

      this.material = tmp;
    }

    let clamp = (number: number, min: number, max: number) => Math.min(Math.max(min, number), max);

    let x = parseInt(String(clamp(pixelPos.x - (pickWindowSize - 1) / 2, 0, width)));
    let y = parseInt(String(clamp(pixelPos.y - (pickWindowSize - 1) / 2, 0, height)));
    let w = parseInt(String(Math.min(x + pickWindowSize, width) - x));
    let h = parseInt(String(Math.min(y + pickWindowSize, height) - y));

    let pixelCount = w * h;
    let buffer = new Uint8Array(4 * pixelCount);

    gl.readPixels(x, y, pickWindowSize, pickWindowSize, gl.RGBA, gl.UNSIGNED_BYTE, buffer);

    renderer.setRenderTarget(null);
    renderer.state.reset();
    renderer.setScissorTest(false);
    gl.disable(gl.SCISSOR_TEST);

    let pixels = buffer;
    let ibuffer = new Uint32Array(buffer.buffer);

    interface Hit {
      pIndex: number;
      pcIndex: number;
      distanceToCenter: number;
      point?: Record<string, unknown>;
    }

    // find closest hit inside pixelWindow boundaries
    let hits: Hit[] = [];
    for (let u = 0; u < pickWindowSize; u++) {
      for (let v = 0; v < pickWindowSize; v++) {
        let offset = u + v * pickWindowSize;
        let distance =
          Math.pow(u - (pickWindowSize - 1) / 2, 2) + Math.pow(v - (pickWindowSize - 1) / 2, 2);

        let pcIndex = pixels[4 * offset + 3];
        pixels[4 * offset + 3] = 0;
        let pIndex = ibuffer[offset];

        if (!(pcIndex === 0 && pIndex === 0) && pcIndex !== undefined && pIndex !== undefined) {
          let hit: Hit = {
            pIndex: pIndex,
            pcIndex: pcIndex,
            distanceToCenter: distance,
          };

          if (params.all) {
            hits.push(hit);
          } else {
            if (hits.length > 0) {
              if (distance < hits[0].distanceToCenter) {
                hits[0] = hit;
              }
            } else {
              hits.push(hit);
            }
          }
        }
      }
    }

    for (let hit of hits) {
      let point: Record<string, unknown> = {};

      if (!nodes[hit.pcIndex]) {
        return null;
      }

      let node = nodes[hit.pcIndex];
      let pc = node.sceneNode!;
      let geometry = node.geometryNode.geometry!;

      for (let attributeName in geometry.attributes) {
        let attribute = geometry.attributes[attributeName] as THREE.BufferAttribute & {
          potree?: { scale: number; offset: number };
        };

        if (attributeName === 'position') {
          let x = attribute.array[3 * hit.pIndex + 0];
          let y = attribute.array[3 * hit.pIndex + 1];
          let z = attribute.array[3 * hit.pIndex + 2];

          let position = new THREE.Vector3(x, y, z);
          position.applyMatrix4(pc.matrixWorld);

          point[attributeName] = position;
        } else if (attributeName === 'indices') {
          // not extracted
        } else {
          let values: number[] | ArrayLike<number> = attribute.array.slice(
            attribute.itemSize * hit.pIndex,
            attribute.itemSize * (hit.pIndex + 1),
          );

          if (attribute.potree) {
            const { scale, offset } = attribute.potree;
            values = Array.from(values).map((v: number) => v / scale + offset);
          }

          point[attributeName] = values;
        }
      }

      hit.point = point;
    }

    if (params.all) {
      return hits.map(hit => hit.point!);
    } else {
      if (hits.length === 0) {
        return null;
      } else {
        return hits[0].point!;
      }
    }
  }

  *getFittedBoxGen(boxNode: THREE.Object3D): Generator<THREE.Object3D | undefined, void, unknown> {
    let shrinkedLocalBounds = new THREE.Box3();
    let worldToBox = boxNode.matrixWorld.clone().invert();

    for (let node of this.visibleNodes) {
      if (!node.sceneNode || !node.geometryNode.geometry) {
        continue;
      }

      const positionAttribute = node.geometryNode.geometry.getAttribute('position');

      let objectToBox = new THREE.Matrix4().multiplyMatrices(
        worldToBox,
        node.sceneNode.matrixWorld,
      );

      let pos = new THREE.Vector4();
      for (let i = 0; i < positionAttribute.count; i++) {
        let x = positionAttribute.getX(i);
        let y = positionAttribute.getY(i);
        let z = positionAttribute.getZ(i);

        pos.set(x, y, z, 1);
        pos.applyMatrix4(objectToBox);

        if (-0.5 < pos.x && pos.x < 0.5) {
          if (-0.5 < pos.y && pos.y < 0.5) {
            if (-0.5 < pos.z && pos.z < 0.5) {
              shrinkedLocalBounds.expandByPoint(new THREE.Vector3(pos.x, pos.y, pos.z));
            }
          }
        }
      }

      yield;
    }

    let fittedPosition = shrinkedLocalBounds
      .getCenter(new THREE.Vector3())
      .applyMatrix4(boxNode.matrixWorld);

    let fitted = new THREE.Object3D();
    fitted.position.copy(fittedPosition);
    fitted.scale.copy(boxNode.scale);
    fitted.rotation.copy(boxNode.rotation);

    let ds = new THREE.Vector3().subVectors(shrinkedLocalBounds.max, shrinkedLocalBounds.min);
    fitted.scale.multiply(ds);

    yield fitted;
  }

  getFittedBox(boxNode: THREE.Object3D, maxLevel = Infinity): THREE.Object3D {
    maxLevel = Infinity;

    let shrinkedLocalBounds = new THREE.Box3();
    let worldToBox = boxNode.matrixWorld.clone().invert();

    for (let node of this.visibleNodes) {
      const level = node.getLevel();
      if (!node.sceneNode || !node.geometryNode.geometry || (level !== null && level > maxLevel)) {
        continue;
      }

      const positionAttribute = node.geometryNode.geometry.getAttribute('position');

      let objectToBox = new THREE.Matrix4().multiplyMatrices(
        worldToBox,
        node.sceneNode.matrixWorld,
      );

      let pos = new THREE.Vector4();
      for (let i = 0; i < positionAttribute.count; i++) {
        let x = positionAttribute.getX(i);
        let y = positionAttribute.getY(i);
        let z = positionAttribute.getZ(i);

        pos.set(x, y, z, 1);
        pos.applyMatrix4(objectToBox);

        if (-0.5 < pos.x && pos.x < 0.5) {
          if (-0.5 < pos.y && pos.y < 0.5) {
            if (-0.5 < pos.z && pos.z < 0.5) {
              shrinkedLocalBounds.expandByPoint(new THREE.Vector3(pos.x, pos.y, pos.z));
            }
          }
        }
      }
    }

    let fittedPosition = shrinkedLocalBounds
      .getCenter(new THREE.Vector3())
      .applyMatrix4(boxNode.matrixWorld);

    let fitted = new THREE.Object3D();
    fitted.position.copy(fittedPosition);
    fitted.scale.copy(boxNode.scale);
    fitted.rotation.copy(boxNode.rotation);

    let ds = new THREE.Vector3().subVectors(shrinkedLocalBounds.max, shrinkedLocalBounds.min);
    fitted.scale.multiply(ds);
    return fitted;
  }

  get progress(): number {
    return this.visibleNodes.length / this.visibleGeometry.length;
  }

  find(name: string): PointCloudChildNode | null {
    let node: PointCloudChildNode | null = null;
    for (let char of name) {
      if (char === 'r') {
        node = this.root as PointCloudChildNode | null;
      } else if (node) {
        const childMap: Record<string, PointCloudChildNode | undefined> = (
          node as unknown as { children: Record<string, PointCloudChildNode | undefined> }
        ).children;
        node = childMap[char] ?? null;
      } else {
        node = null;
      }
    }

    return node;
  }
}
